// components/notifications/NotificationsModal.tsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, ChevronDown, ChevronUp, Search, Bell } from 'lucide-react'
import { Button, Text } from '@mantine/core'
import { toast } from 'react-hot-toast'
import moment from 'moment'
import { NotificationsModalSkeleton } from '../ui/Skeleton'
import {
  useNotificationsByView,
  useArchiveNotification,
  useDeferNotification,
  useMarkAsSeen,
  useMarkNotificationAsReadWithVersion,
} from '@/hooks/api/useNotifications'
import {
  NotificationSummary,
  NotificationView,
  TeamFeedResponse,
  MyWorkResponse,
  ArchiveResponse,
} from '@/types/api/notifications'

// Global sets to track seen notifications across renders and components
const globalSeenNotifications = new Set<string>()
const globalSeenInProgress = new Set<string>()

interface NotificationsModalProps {
  notifications: NotificationSummary[]
  isLoading: boolean
  onClose: () => void
  onRefresh: () => void
}

interface GroupedNotification {
  data_id: string
  notifications: NotificationSummary[]
  hasUnread: boolean
  timestamp: string
  isSeen?: boolean
  isDeferred?: boolean
  deferredUntil?: number | null
}

const transformThreadLikeToNotification = (
  threadLike: any
): NotificationSummary => {
  const latest = threadLike.latest_notification || {}

  const notification: NotificationSummary = {
    id: latest.notification_id || threadLike.thread_id || '',
    title: latest.title || 'Notification',
    message: latest.body || '',
    status: latest.lifecycle_state === 'unread' ? 'unread' : 'read',
    priority: latest.priority || '',
    created_at: latest.createdAt || new Date().toISOString(),
    updated_by_name: latest.actor_name || 'System',
    related_entity_id: threadLike.case_id || latest.data_id || '',
    type: latest.data_type || 'update',
    action_url: latest.action_url || undefined,
    read_at: latest.read_at || undefined,
  }

  const notificationWithData = notification as any
  notificationWithData._thread = threadLike
  notificationWithData._notification = latest
  notificationWithData.version = latest.version || 1
  notificationWithData.notification_id =
    latest.notification_id || threadLike.thread_id || ''
  notificationWithData.lifecycle_state = latest.lifecycle_state || 'unread'
  notificationWithData.priority = latest.priority || ''
  notificationWithData.data_type = latest.data_type || 'update'
  notificationWithData.data_group = latest.data_group || ''
  notificationWithData.classification = latest.classification || ''
  notificationWithData.placement = latest.placement || ''
  notificationWithData.scope_level = latest.scope_level || ''
  notificationWithData.stage = latest.stage || ''
  notificationWithData.recipient_entity = latest.recipient_entity || ''
  notificationWithData.requires_action = latest.requires_action || false
  notificationWithData.actor_name = latest.actor_name || 'System'
  notificationWithData.recipient_id = latest.recipient_id || ''
  notificationWithData.precinct_id = latest.precinct_id || ''
  notificationWithData.server_timestamp = latest.server_timestamp || Date.now()
  notificationWithData.payload = latest.payload || {}
  notificationWithData.case_id = threadLike.case_id || latest.data_id || ''
  notificationWithData.data_id = latest.data_id || threadLike.case_id || ''

  notificationWithData.isDeferred =
    latest.lifecycle_state === 'deferred' ||
    (latest.deferred_until && latest.deferred_until > Date.now())
  notificationWithData.deferredUntil = latest.deferred_until || null

  return notification
}

const groupNotifications = (
  notifications: NotificationSummary[]
): GroupedNotification[] => {
  if (!notifications || notifications.length === 0) return []

  const grouped = notifications.reduce(
    (acc, notification) => {
      const notificationWithData = notification as any

      let groupKey = notification.related_entity_id || notification.id

      if (notificationWithData.case_id?.startsWith('shift-')) {
        groupKey = notificationWithData.case_id
      } else if (notificationWithData.data_id?.startsWith('shift-')) {
        groupKey = notificationWithData.data_id
      } else if (notificationWithData.case_id) {
        groupKey = notificationWithData.case_id
      } else if (notificationWithData.data_id) {
        groupKey = notificationWithData.data_id
      }

      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(notification)
      return acc
    },
    {} as Record<string, NotificationSummary[]>
  )

  return Object.entries(grouped).map(([data_id, notifications]) => {
    const isDeferred = notifications.some((n: any) => {
      const deferredUntil = n.deferredUntil || (n as any).deferred_until
      const isDeferredStatus = (n as any).lifecycle_state === 'deferred'
      const isExpired = deferredUntil && deferredUntil < Date.now()

      return (
        isDeferredStatus ||
        (deferredUntil && deferredUntil > Date.now() && !isExpired)
      )
    })

    return {
      data_id,
      notifications,
      hasUnread: notifications.some(n => n.status === 'unread'),
      timestamp: notifications[0]?.created_at || '',
      isSeen: notifications.every(n => n.status === 'read'),
      isDeferred: isDeferred,
      deferredUntil: notifications.reduce((latest, n: any) => {
        const until = n.deferredUntil || n.deferred_until
        if (until && (!latest || until > latest)) return until
        return latest
      }, null),
    }
  })
}

function isTeamFeedResponse(data: any): data is TeamFeedResponse {
  return data?.data?.threads !== undefined
}

function isMyWorkResponse(data: any): data is MyWorkResponse {
  return (
    data?.data?.active_cases !== undefined ||
    data?.data?.shift_alerts !== undefined ||
    data?.data?.requires_action !== undefined ||
    data?.data?.informational !== undefined
  )
}

function isArchiveResponse(data: any): data is ArchiveResponse {
  return (
    data?.data?.notifications !== undefined && data?.data?.threads === undefined
  )
}

const getPriorityConfig = (priority?: string) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return {
        label: 'High',
        dot: 'bg-[#FA5252]',
        border: 'border-[#FA5252]',
        bg: 'bg-[#FFF5F5]',
        seenBg: 'bg-[#FFF5F5]/50',
      }
    case 'medium':
      return {
        label: 'Medium',
        dot: 'bg-[#FF922B]',
        border: 'border-[#FF922B]',
        bg: 'bg-[#FFF4E6]',
        seenBg: 'bg-[#FFF4E6]/50',
      }
    case 'low':
      return {
        label: 'Low',
        dot: 'bg-gray-400',
        border: 'border-gray-200',
        bg: 'bg-gray-50',
        seenBg: 'bg-gray-50/50',
      }
    default:
      return {
        label: 'Low',
        dot: 'bg-gray-400',
        border: 'border-gray-200',
        bg: 'bg-gray-50',
        seenBg: 'bg-gray-50/50',
      }
  }
}

const NotificationItem: React.FC<{
  group: GroupedNotification
  isExpanded: boolean
  isMarkingRead: boolean
  isDeferred: boolean
  isDeferring: boolean
  isSeen: boolean
  isProcessing: boolean
  onToggleExpand: (id: string) => void
  onArchive: (group: GroupedNotification) => void
  onDefer: (group: GroupedNotification) => void
  onViewDetails: (group: GroupedNotification) => void
  onInView: (group: GroupedNotification) => void
  activeTab: string
}> = ({
  group,
  isExpanded,
  isMarkingRead,
  isDeferred,
  isDeferring,
  isSeen,
  isProcessing,
  onToggleExpand,
  onArchive,
  onDefer,
  onViewDetails,
  onInView,
  activeTab,
}) => {
  const latest = group.notifications[0]
  const priority = getPriorityConfig(latest?.priority)
  const notificationRef = useRef<HTMLDivElement>(null)

  // IntersectionObserver to mark as seen - with global deduplication
  useEffect(() => {
    if (!notificationRef.current || !group.hasUnread) return

    const notificationWithData = latest as any
    const notificationId =
      notificationWithData.notification_id || latest?.id || ''
    const version = notificationWithData.version || 1
    const requestKey = `${notificationId}-${version}`

    if (
      globalSeenNotifications.has(notificationId) ||
      globalSeenInProgress.has(requestKey)
    ) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && group.hasUnread) {
            if (
              globalSeenNotifications.has(notificationId) ||
              globalSeenInProgress.has(requestKey)
            ) {
              return
            }

            if (timeoutId) {
              clearTimeout(timeoutId)
              timeoutId = null
            }

            timeoutId = setTimeout(() => {
              if (
                !globalSeenNotifications.has(notificationId) &&
                !globalSeenInProgress.has(requestKey)
              ) {
                globalSeenInProgress.add(requestKey)
                onInView(group)
              }
              timeoutId = null
            }, 1500)
          } else {
            if (timeoutId) {
              clearTimeout(timeoutId)
              timeoutId = null
            }
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(notificationRef.current)

    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [group.hasUnread, group, onInView, latest])

  const formatCaseId = (case_id: string, createdAt: string) => {
    if (!case_id || !createdAt) return '—'
    const date = new Date(createdAt)
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, '')
    const suffix = case_id.slice(-4).toUpperCase()
    return `C${formattedDate}-${suffix}`
  }

  return (
    <div
      ref={notificationRef}
      className={`mb-4 border border-[#D7D7D7] rounded-xl hover:shadow-md transition-shadow ${
        !isSeen ? 'bg-blue-50/50' : 'bg-white'
      } ${isProcessing && activeTab !== 'archive' ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <h3 className="font-bold text-[18px] text-[#23476B]">
            {formatCaseId(group.data_id, group.timestamp)}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${priority.dot}`} />
            <span className="text-[14px]">{priority.label}</span>
            {isDeferred && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                Deferred{' '}
                {group.deferredUntil
                  ? `until ${moment(group.deferredUntil).format('h:mm A')}`
                  : '✓'}
              </span>
            )}
            {!isSeen && !isDeferred && (
              <span className="ml-2 w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            )}
          </div>
        </div>

        <p className="mt-2 text-[15px] leading-7 text-[#2E2E2E] line-clamp-2">
          {latest?.message}
        </p>

        {/* Activity Bar */}
        <div
          onClick={() => onToggleExpand(group.data_id)}
          className={`mt-4 rounded-lg border ${priority.border} ${
            !isSeen ? 'bg-white/80' : 'bg-gray-50/50'
          } cursor-pointer hover:opacity-80 transition-opacity`}
        >
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase text-[#6A6A6A]">
                updated by
              </span>
              <div className="w-6 h-6 rounded-full bg-[#24476B] text-white flex items-center justify-center text-[11px]">
                {(latest?.updated_by_name || 'S')[0]}
              </div>
              <span className="font-medium text-sm">
                {latest?.updated_by_name || 'System'}
              </span>
              <span className="text-sm text-[#7A7A7A]">
                {moment(new Date(latest?.created_at || '')).format('h:mm A')}
              </span>
            </div>
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>

          {/* Timeline */}
          {isExpanded && (
            <div className="px-6 pb-5">
              <div className="relative ml-4 border-l border-[#D7D7D7]">
                {group.notifications.map((n, index) => (
                  <div
                    key={n.id}
                    className={`relative pl-6 ${
                      index < group.notifications.length - 1 ? 'pb-6' : ''
                    }`}
                  >
                    <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-[3px] border-[#24476B]" />
                    <div className="flex gap-2 flex-wrap text-sm">
                      <span className="font-semibold">{n.title}</span>
                      <span className="text-[#7A7A7A]">
                        {moment(new Date(n.created_at)).format(
                          'MMM D, YYYY h:mm A'
                        )}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-[#5D5E67]">{n.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 mt-5">
          {/* Only show archive button if not in archive tab */}
          {activeTab !== 'archive' && (
            <button
              disabled={
                !group.hasUnread || isMarkingRead || isProcessing || isDeferred
              }
              onClick={() => onArchive(group)}
              className={`text-[15px] ${
                !group.hasUnread || isMarkingRead || isProcessing || isDeferred
                  ? 'text-[#B0B0B0] cursor-not-allowed'
                  : 'text-[#2E2E2E] hover:text-[#FA5252]'
              } transition-colors cursor-pointer`}
            >
              {isMarkingRead ? 'Archiving...' : 'Archive'}
            </button>
          )}

          {/* Show defer button only if not in archive tab */}
          {activeTab !== 'archive' && (
            <Button
              disabled={isDeferred || isDeferring || isProcessing}
              onClick={() => onDefer(group)}
              styles={{
                root: {
                  background: isDeferred ? '#E8F5E9' : '#E9F1FF',
                  color: isDeferred ? '#2E7D32' : '#2E2E2E',
                  height: 42,
                  borderRadius: 8,
                  opacity: isDeferred || isDeferring || isProcessing ? 0.5 : 1,
                  border: isDeferred ? '1px solid #4CAF50' : 'none',
                },
              }}
            >
              {isDeferring
                ? 'Deferring...'
                : isDeferred
                  ? 'Deferred ✓'
                  : 'Defer'}
            </Button>
          )}

          <Button
            onClick={() => onViewDetails(group)}
            styles={{
              root: {
                background: '#000',
                color: '#fff',
                height: 42,
                borderRadius: 8,
                paddingLeft: 22,
                paddingRight: 22,
                '&:hover': {
                  background: '#333',
                },
              },
            }}
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  )
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  notifications: _initialNotifications, // Prefix with underscore to indicate intentionally unused
  isLoading: initialLoading,
  onClose,
  onRefresh,
}) => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<NotificationView>('team_feed')
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const [markingAsRead, setMarkingAsRead] = useState<Record<string, boolean>>(
    {}
  )
  const [deferring, setDeferring] = useState<Record<string, boolean>>({})
  const [deferredItems, setDeferredItems] = useState<Record<string, boolean>>(
    {}
  )
  const [search, setSearch] = useState('')
  const [isClosing, setIsClosing] = useState(false)
  const [processingGroups] = useState<Record<string, boolean>>({}) // Remove unused setter
  const [groupedNotificationsState, setGroupedNotifications] = useState<
    GroupedNotification[]
  >([])

  const processingRef = useRef<Set<string>>(new Set())

  const { data, isLoading, refetch } = useNotificationsByView(activeTab)
  const archiveMutation = useArchiveNotification()
  const deferMutation = useDeferNotification()
  const markAsSeenMutation = useMarkAsSeen()
  const markAsReadMutation = useMarkNotificationAsReadWithVersion()

  const notifications = useMemo(() => {
    if (!data?.data) return []

    if (isTeamFeedResponse(data)) {
      if (data.data.threads && data.data.threads.length > 0) {
        return data.data.threads.map(transformThreadLikeToNotification)
      }
      return data.data.notifications ?? []
    }

    if (isMyWorkResponse(data)) {
      const responseData = data.data as any

      if (responseData.threads && responseData.threads.length > 0) {
        return responseData.threads.map(transformThreadLikeToNotification)
      }

      const allNotifications: NotificationSummary[] = []

      if (responseData.active_cases?.length > 0) {
        if (responseData.active_cases[0]?.latest_notification) {
          const transformed = responseData.active_cases.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.active_cases)
        }
      }

      if (responseData.shift_alerts?.length > 0) {
        if (responseData.shift_alerts[0]?.latest_notification) {
          const transformed = responseData.shift_alerts.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.shift_alerts)
        }
      }

      if (responseData.requires_action?.length > 0) {
        if (responseData.requires_action[0]?.latest_notification) {
          const transformed = responseData.requires_action.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.requires_action)
        }
      }

      if (responseData.informational?.length > 0) {
        if (responseData.informational[0]?.latest_notification) {
          const transformed = responseData.informational.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.informational)
        }
      }

      return allNotifications
    }

    // Archive
    if (isArchiveResponse(data)) {
      return data.data.notifications ?? []
    }

    return []
  }, [data])

  // Update grouped notifications when data changes
  useEffect(() => {
    const grouped = groupNotifications(notifications)
    setGroupedNotifications(prev => {
      const deferredMap = new Map()
      prev.forEach(g => {
        if (g.isDeferred) {
          deferredMap.set(g.data_id, true)
        }
      })
      return grouped.map(g => ({
        ...g,
        isDeferred: deferredMap.get(g.data_id) || g.isDeferred || false,
      }))
    })
  }, [notifications])

  // Sync deferred state when data changes
  useEffect(() => {
    if (!data?.data) return

    // Check if any notifications are deferred in the new data
    const deferredFromData = new Set<string>()

    if (isTeamFeedResponse(data) && data.data.threads) {
      data.data.threads.forEach((thread: any) => {
        if (thread.latest_notification?.lifecycle_state === 'deferred') {
          deferredFromData.add(thread.thread_id)
        }
      })
    }

    if (isMyWorkResponse(data)) {
      const responseData = data.data as any
      const allItems = [
        ...(responseData.threads || []),
        ...(responseData.active_cases || []),
        ...(responseData.shift_alerts || []),
        ...(responseData.requires_action || []),
        ...(responseData.informational || []),
      ]

      allItems.forEach((item: any) => {
        if (item.latest_notification?.lifecycle_state === 'deferred') {
          deferredFromData.add(item.case_id || item.thread_id)
        }
      })
    }

    // Update deferred state
    setDeferredItems(prev => {
      const newState = { ...prev }
      // Remove items that are no longer deferred
      Object.keys(newState).forEach(key => {
        if (!deferredFromData.has(key)) {
          delete newState[key]
        }
      })
      // Add new deferred items
      deferredFromData.forEach(key => {
        newState[key] = true
      })
      return newState
    })
  }, [data])

  // Get badge count safely
  const badgeCount = useMemo(() => {
    if (!data?.data) return 0

    if (isMyWorkResponse(data)) {
      if (data.data.badge_counts) {
        return data.data.badge_counts.badge ?? 0
      }
      const responseData = data.data as any
      const allNotifications = [
        ...(responseData.requires_action ?? []),
        ...(responseData.active_cases ?? []),
        ...(responseData.shift_alerts ?? []),
        ...(responseData.informational ?? []),
      ]
      return allNotifications.filter(
        (n: any) => n.lifecycle_state === 'unread' || n.status === 'unread'
      ).length
    }

    if (isTeamFeedResponse(data)) {
      if (data.data.threads) {
        return data.data.threads.filter(
          (thread: any) =>
            thread.latest_notification?.lifecycle_state === 'unread'
        ).length
      }
      return (
        data.data.notifications?.filter((n: any) => n.status === 'unread')
          .length ?? 0
      )
    }

    return 0
  }, [data])

  const filteredNotifications = groupedNotificationsState.filter(group => {
    if (!search.trim()) return true
    const searchLower = search.toLowerCase()
    return group.notifications.some(n =>
      n.updated_by_name?.toLowerCase().includes(searchLower)
    )
  })

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const handleNotificationInView = useCallback(
    (group: GroupedNotification) => {
      if (!group.hasUnread) return

      const latest = group.notifications[0]
      if (!latest) return

      const notificationWithData = latest as any
      const notificationId = notificationWithData.notification_id || latest.id
      const version = notificationWithData.version || 1
      const requestKey = `${notificationId}-${version}`

      if (
        globalSeenNotifications.has(notificationId) ||
        globalSeenInProgress.has(requestKey)
      ) {
        return
      }

      markAsSeenMutation.mutate(
        {
          notificationId,
          version,
          entity: 'dispatcher',
        },
        {
          onSuccess: () => {
            globalSeenNotifications.add(notificationId)
            globalSeenInProgress.delete(requestKey)
            setGroupedNotifications(prev =>
              prev.map(g => {
                if (g.data_id === group.data_id) {
                  return {
                    ...g,
                    hasUnread: false,
                    isSeen: true,
                    notifications: g.notifications.map(n => ({
                      ...n,
                      status: 'read' as const,
                    })),
                  }
                }
                return g
              })
            )
          },
          onError: () => {
            globalSeenInProgress.delete(requestKey)
          },
        }
      )
    },
    [markAsSeenMutation]
  )

  const markGroupAsRead = useCallback(
    async (group: GroupedNotification) => {
      if (!group.hasUnread) return

      const operationKey = `archive-${group.data_id}`

      if (processingRef.current.has(operationKey)) {
        console.log('Already processing this group')
        return
      }

      processingRef.current.add(operationKey)

      setMarkingAsRead(prev => ({ ...prev, [group.data_id]: true }))
      setGroupedNotifications(prev =>
        prev.filter(g => g.data_id !== group.data_id)
      )

      try {
        const unreadNotifications = group.notifications.filter(
          n => n.status === 'unread'
        )

        const results = await Promise.allSettled(
          unreadNotifications.map(notification => {
            const notificationWithData = notification as any
            const notificationId =
              notificationWithData.notification_id || notification.id
            const version = notificationWithData.version || 1

            return archiveMutation.mutateAsync({
              notificationId,
              entity: 'dispatcher',
              version,
            })
          })
        )

        const hasConflicts = results.some(
          (r): r is PromiseRejectedResult =>
            r.status === 'rejected' &&
            (r.reason as any)?.response?.data?.status === 'conflict'
        )

        if (hasConflicts) {
          toast.error(
            'Some notifications were updated elsewhere. Refreshing...'
          )
          setGroupedNotifications(prev => [...prev, group])
          await refetch()
          onRefresh()
        } else {
          toast.success('Notifications archived')
          await refetch()
          onRefresh()
        }
      } catch (error: any) {
        setGroupedNotifications(prev => [...prev, group])

        if (error?.response?.data?.status === 'conflict') {
          toast.error('Notification was updated elsewhere. Please refresh.')
          await refetch()
          onRefresh()
        } else {
          toast.error('Failed to archive notification')
        }
      } finally {
        setMarkingAsRead(prev => ({ ...prev, [group.data_id]: false }))
        processingRef.current.delete(operationKey)
      }
    },
    [archiveMutation, refetch, onRefresh]
  )

  const handleDefer = useCallback(
    async (group: GroupedNotification) => {
      if (deferredItems[group.data_id]) return

      const operationKey = `defer-${group.data_id}`

      if (processingRef.current.has(operationKey)) {
        return
      }

      processingRef.current.add(operationKey)

      setDeferring(prev => ({ ...prev, [group.data_id]: true }))

      try {
        const latest = group.notifications[0]
        if (!latest) return

        const notificationWithData = latest as any
        const notificationId = notificationWithData.notification_id || latest.id
        const version = notificationWithData.version || 1

        const deferredUntil = Date.now() + 3600000

        const response = await deferMutation.mutateAsync({
          notificationId,
          version,
          entity: 'dispatcher',
          deferredUntil,
        })

        const updatedNotification = response?.data?.data || response?.data

        if (updatedNotification) {
          setGroupedNotifications(prev =>
            prev.map(g => {
              if (g.data_id === group.data_id) {
                return {
                  ...g,
                  isDeferred: true,
                  deferredUntil:
                    updatedNotification.deferred_until || deferredUntil,
                  hasUnread: false,
                  isSeen: true,
                }
              }
              return g
            })
          )

          setDeferredItems(prev => ({
            ...prev,
            [group.data_id]: true,
          }))

          toast.success('Notification deferred for 1 hour')
          await refetch()
          onRefresh()
        }
      } catch (error: any) {
        setDeferredItems(prev => {
          const newState = { ...prev }
          delete newState[group.data_id]
          return newState
        })
        // Restore the group if it was modified
        setGroupedNotifications(prev =>
          prev.map(g => {
            if (g.data_id === group.data_id) {
              return {
                ...g,
                isDeferred: false,
                deferredUntil: null,
                hasUnread: true,
                isSeen: false,
              }
            }
            return g
          })
        )

        if (error?.response?.data?.status === 'conflict') {
          toast.error('Notification was updated elsewhere. Please refresh.')
          await refetch()
          onRefresh()
        } else {
          toast.error('Failed to defer notification')
        }
      } finally {
        setDeferring(prev => ({ ...prev, [group.data_id]: false }))
        processingRef.current.delete(operationKey)
      }
    },
    [deferredItems, deferMutation, refetch, onRefresh]
  )

  const viewCaseDetails = useCallback(
    async (group: GroupedNotification) => {
      const caseId = group.data_id

      const operationKey = `view-${caseId}`

      if (processingRef.current.has(operationKey)) {
        console.log('Already viewing this case')
        return
      }

      processingRef.current.add(operationKey)

      try {
        if (group.hasUnread) {
          const latest = group.notifications[0]
          if (latest) {
            const notificationWithData = latest as any
            const notificationId =
              notificationWithData.notification_id || latest.id
            const version = notificationWithData.version || 1

            await markAsReadMutation.mutateAsync({
              notificationId,
              version,
              entity: 'dispatcher',
            })

            setGroupedNotifications(prev =>
              prev.map(g => {
                if (g.data_id === group.data_id) {
                  return {
                    ...g,
                    hasUnread: false,
                    isSeen: true,
                    notifications: g.notifications.map(n => ({
                      ...n,
                      status: 'read' as const,
                    })),
                  }
                }
                return g
              })
            )
          }
        }

        onClose()

        setTimeout(() => {
          if (caseId.startsWith('cas-')) {
            navigate(`/cases/${caseId}`)
          } else if (caseId.startsWith('ar-')) {
            navigate('/ambulance-request')
          } else {
            navigate('/cases')
          }
        }, 100)
      } catch (error: any) {
        console.error('Failed to view case details:', error)
        if (error?.response?.data?.status === 'conflict') {
          toast.error('Notification was updated elsewhere. Refreshing...')
          await refetch()
        } else {
          toast.error('Failed to view case details')
        }
      } finally {
        processingRef.current.delete(operationKey)
      }
    },
    [navigate, onClose, refetch, markAsReadMutation]
  )

  const renderEmptyState = () => {
    const messages = {
      archive: {
        title: 'No Archived Notifications',
        description: 'Resolved cases and archived items will appear here.',
      },
      team_feed: {
        title: 'No Team Feed Notifications',
        description: 'Team activities will appear here.',
      },
      my_work: {
        title: 'Nothing Assigned Yet',
        description: 'Cases that require your attention will appear here.',
      },
    }

    const message = messages[activeTab]

    return (
      <div className="h-full flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Text size="xl">📁</Text>
        </div>
        <Text fw={600} size="sm">
          {message.title}
        </Text>
        <Text size="xs" c="dimmed">
          {message.description}
        </Text>
      </div>
    )
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
      setIsClosing(false)
    }, 300)
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-[90]" onClick={handleClose} />

      <div
        className={`fixed top-0 right-0 z-[100] h-full w-[600px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isClosing ? 'translate-x-full' : 'translate-x-0'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#E8E8E8] flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-semibold text-[#2E2E2E]">
                Notifications
              </h1>
              <Bell size={18} className="text-[#8C8C8C]" />
              {badgeCount > 0 && (
                <span className="bg-[#FA5252] text-white text-xs rounded-full px-2 py-0.5">
                  {badgeCount}
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              className="rounded-md p-1 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-3 pt-4 flex-shrink-0">
          <div className="grid grid-cols-3 bg-[#F4F4F6] p-1 rounded-xl">
            {[
              { key: 'team_feed', label: 'Team Feed' },
              { key: 'my_work', label: 'My Work' },
              { key: 'archive', label: 'Archive' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key as NotificationView)
                  setExpandedGroups({})
                  refetch()
                }}
                className={`h-[54px] rounded-xl text-[15px] transition-colors cursor-pointer
                ${
                  activeTab === tab.key
                    ? 'bg-white border border-[#CFCFD4] text-[#24476B] font-semibold'
                    : 'text-[#2C2C2C] hover:bg-[#EAEAEA]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-4 flex-shrink-0">
          <div className="flex gap-3">
            <div className="flex-1 h-[56px] border border-[#A4A4A4] rounded-xl px-4 flex items-center gap-3">
              <Search size={20} className="text-[#8C8C8C]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="flex-1 outline-none text-sm bg-transparent"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="text-[#8C8C8C] hover:text-[#2E2E2E] cursor-pointer"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 pb-5">
          {isLoading || initialLoading ? (
            <NotificationsModalSkeleton />
          ) : filteredNotifications.length === 0 ? (
            renderEmptyState()
          ) : (
            filteredNotifications.map(group => {
              const isExpanded = expandedGroups[group.data_id] || false
              const isMarkingRead = markingAsRead[group.data_id] || false
              const isDeferred =
                deferredItems[group.data_id] || group.isDeferred || false
              const isDeferring = deferring[group.data_id] || false
              const isSeen = group.isSeen || !group.hasUnread
              const isProcessing = processingGroups[group.data_id] || false

              return (
                <NotificationItem
                  key={group.data_id}
                  group={group}
                  isExpanded={isExpanded}
                  isMarkingRead={isMarkingRead}
                  isDeferred={isDeferred}
                  isDeferring={isDeferring}
                  isSeen={isSeen}
                  isProcessing={isProcessing}
                  onToggleExpand={toggleGroupExpansion}
                  onArchive={markGroupAsRead}
                  onDefer={handleDefer}
                  onViewDetails={viewCaseDetails}
                  onInView={handleNotificationInView}
                  activeTab={activeTab}
                />
              )
            })
          )}
        </div>
      </div>
    </>
  )
}

export default NotificationsModal
